import { Link } from '@tanstack/react-router'
import { ChevronRight, MapPin, Star, Store } from 'lucide-react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useCrosspostingStatus } from '~/entities/crossposting/model/use-crossposting-status'
import { useListings } from '~/entities/listing/model/use-listings'
import { CrosspostingUnavailable } from '~/features/crossposting/components/crossposting-unavailable'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import type { Listing } from '~/shared/types'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5', 's6']

export function CrosspostingPlaces() {
  const { data: business, isLoading: isBusinessLoading } = useCurrentBusiness()
  const { listings, isLoading: isListingsLoading } = useListings(business?.id ?? '')
  const { data: status, isLoading: isStatusLoading } = useCrosspostingStatus(business?.id ?? '')

  const isLoading = isBusinessLoading || isListingsLoading || isStatusLoading

  if (!isLoading && status && !status.enabled) {
    return (
      <div className="space-y-6">
        <PageHeader title={m.crossposting()} description={m.crosspostingSelectPlaceDescription()} />
        <CrosspostingUnavailable />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader title={m.crossposting()} description={m.crosspostingSelectPlaceDescription()} />

      {isLoading ? (
        <div className="space-y-2">
          {SKELETON_KEYS.map((key) => (
            <Skeleton key={key} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <EmptyPlaces />
      ) : (
        <div className="space-y-2">
          {listings.map((listing) => (
            <PlaceRow key={listing.id} listing={listing} />
          ))}
        </div>
      )}
    </div>
  )
}

function PlaceRow({ listing }: { listing: Listing }) {
  const isDraft = listing.isDraft || listing.status === 'Draft'

  return (
    <Link
      to="/dashboard/crossposting/$listingId"
      params={{ listingId: listing.id }}
      className="group flex items-center gap-4 rounded-xl border bg-card p-3 text-card-foreground beacon-interactive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
    >
      {listing.coverImageUrl ? (
        <img
          src={listing.coverImageUrl}
          alt={listing.name}
          className="h-14 w-20 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Store className="h-6 w-6 text-muted-foreground/40" />
        </div>
      )}

      <div className="flex min-w-0 flex-1 items-center gap-3">
        <h3 className="max-w-[45%] shrink-0 truncate font-semibold tracking-tight">
          {listing.name}
        </h3>
        <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground text-sm">
          <MapPin className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {listing.location.city}, {countryDisplayName(listing.location.country)}
          </span>
        </span>
        {listing.rating !== undefined && listing.rating > 0 && (
          <span className="flex shrink-0 items-center gap-1 text-sm">
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            <span className="font-medium">{listing.rating.toFixed(1)}</span>
            {listing.reviewCount ? (
              <span className="text-muted-foreground">({listing.reviewCount})</span>
            ) : null}
          </span>
        )}
        {isDraft && (
          <Badge variant="secondary" className="shrink-0">
            {m.draft()}
          </Badge>
        )}
      </div>

      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 ease-spring group-hover:translate-x-0.5" />
    </Link>
  )
}

function EmptyPlaces() {
  return (
    <Card className="flex flex-col items-center gap-4 p-10 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Store className="h-6 w-6" />
      </span>
      <div className="space-y-1.5">
        <h3 className="font-semibold tracking-tight">{m.crosspostingNoPlacesTitle()}</h3>
        <p className="text-sm text-muted-foreground">{m.crosspostingNoPlacesDescription()}</p>
      </div>
      <Button asChild>
        <Link to="/setup/profile" search={{ kind: 'place' }}>
          {m.crosspostingCreatePlace()}
        </Link>
      </Button>
    </Card>
  )
}
